import React, { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { useAuth } from 'services/auth-service';
import Modal from 'components/elements/Modal';
import * as yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from 'components/elements/Form/Input/input';
import SurveysIcon from 'assets/Icons/sidebarIcons/SurveysIcon';
import { useQuery } from 'react-query';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import BuildingService from 'services/hooks/building-service';
import { SurveyProps } from 'services/hooks/survey-service';
import useSaveSurvey from './crud/useSave';
import SelectEntity from 'components/structure/Common/ListsEntity/SelectEntity';
import PrimaryButton from 'components/elements/Form/button/primaryButton';

type CreateProps = {
  entityName: string;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setEditUUID?: React.Dispatch<React.SetStateAction<string>>;
  createQuery: (newSurvey: SurveyProps) => Promise<SurveyProps>;
  updateQuery: (uuid: string | undefined, updatedSurvey: SurveyProps) => Promise<SurveyProps>;
  editQuery?: (uuid: string | undefined) => Promise<SurveyProps>;
  editUUID?: string;
  queryName: string;
};

/* The code is defining a validation schema using the `yup` library. If these validation rules are not met, an error message will be displayed. */
const CreateSurveySchema = yup.object().shape({
  code: yup.string().required('code is required'),
  buildingUuid: yup.string().required('Building is required')
});

export default function Save({
  entityName,
  showModal,
  setEditUUID,
  setShowModal,
  createQuery,
  updateQuery,
  editQuery,
  editUUID,
  queryName
}: CreateProps) {
  /* The code is using the `useAuth` hook to access the authentication state. It is destructuring the `user` property from the state 
  object returned by the `useAuth` hook. This allows the code to access the currently logged-in user's information, such as their role. */
  const {
    state: { user }
  } = useAuth();
  const [getSurveyResult, setGetSurveyResult] = useState<SurveyProps | null>(null);
  const [disabled, setdisabled] = useState(false);

  const { isSuccess, data } = useQuery<SurveyProps | undefined, Error>(
    'survey',
    () => (editUUID ? editQuery?.(editUUID) : undefined),
    {
      enabled: editUUID ? true : false,
      onError: (err: Error) => {
        if (err instanceof AxiosError) {
          // Handle Axios-specific errors
          console.log(err.response?.data || err);
        } else {
          // Handle generic errors
          console.error(err);
        }
      }
    }
  );

  /* The `useForm` hook from the `react-hook-form` library is being used to create a form with form validation. */
  const methods = useForm({
    defaultValues: getSurveyResult
      ? {
          uuid: getSurveyResult?.uuid,
          code: getSurveyResult?.code,
          body: getSurveyResult?.body,
          enabled: getSurveyResult?.enabled,
          buildingUuid: getSurveyResult?.buildingUuid
        }
      : {
          uuid: '',
          code: '',
          body: '',
          enabled: '',
          buildingUuid: ''
        },
    resolver: yupResolver(CreateSurveySchema)
  });

  useEffect(() => {
    if (data && editUUID) {
      setGetSurveyResult(data);
      /* `methods.reset(data);` is being used to set the initial values of the form fields to the values retrieved from the `data` object. 
      The `data` object contains the Survey details fetched from the API when editing an existing Survey. 
      By calling `methods.reset()` with the desired initial values, the form fields will be populated with the fetched data when the form is rendered. */
      methods.reset(data);
    }
  }, [data, editUUID, methods]);

  /* The code is creating a mutation function called `saveSurveyMutation` using the `useSaveSurvey` hook. This hook takes in several parameters: */
  const saveSurveyMutation = useSaveSurvey({
    createQuery,
    updateQuery,
    queryName,
    onSuccess: () => {
      setShowModal(false);
      setdisabled(false);
      if (setEditUUID) setEditUUID('');
    }
  });

  /* The `onSubmit` function takes a `SurveyProps` object as an argument, extracts specific properties from it, and passes them to the 
  `saveSurveyMutation.mutate` function. */
  const onSubmit = async (e: SurveyProps) => {
    setdisabled(true);
    saveSurveyMutation.mutate({
      uuid: e?.uuid,
      code: e.code,
      body: e?.body,
      buildingUuid: e.buildingUuid,
      enabled: e?.enabled
    });
  };

  return !editUUID || isSuccess ? (
    <>
      <Modal
        isOpen={showModal}
        onRequestClose={() => {
          setShowModal(false);
          if (setEditUUID) setEditUUID('');
        }}
        title={entityName}
      >
        <form onSubmit={methods.handleSubmit(onSubmit)} className={`text-left items-start`}>
          <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4 mr-8'}>
            {(user.role === 'SUPER' || user.role === 'ACCOUNT') && (
              <SelectEntity
                Controller={Controller}
                control={methods.control}
                label="Choose an Building"
                placeholder="Select Building"
                queryname="buildings"
                name="buildingUuid"
                defaultValue={getSurveyResult?.buildingUuid}
                errors={methods.formState.errors}
                disabled={editUUID && true}
                query={BuildingService.findAllBuildings}
              />
            )}
            <Input
              label="Code"
              name="code"
              type="text"
              // data-testid="example"
              placeholder="Bline"
              autoFocus
              methods={methods}
            />
            <Input
              label="Enable"
              name="enabled"
              type="checkbox"
              // data-testid="example"
              autoFocus
              methods={methods}
            />
            <br />
            <div className="col-span-1 sm:col-span-2">
              <label className="text-neutral7 uppercase" htmlFor="body">
                Body
              </label>
              <textarea
                {...methods.register('body')}
                className="overflow-y-scroll"
                id="body"
                name="body"
                rows={10}
              />
            </div>
          </div>
          <PrimaryButton
            buttonLabel={entityName}
            iconLeft={<SurveysIcon />}
            variant="default"
            size={'mt-4 px-6 w-auto gap-2'}
            disabled={disabled}
          />
        </form>
      </Modal>
    </>
  ) : (
    <LoadingSpinner />
  );
}
